import React, {
  Fragment,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { useHistory } from 'react-router-dom'
import useKeyPress from '@scentregroup/shared/hooks/use-key-press'
import { useEvolvedContext } from '../../context'
import SearchResults from './search-results'
import SearchContainer from './search-container'
import SearchModalControls from './search-modal-controls'
import {
  isEmbeddedSearchProps,
  EmbeddedSearchProps,
  ModalSearchProps,
  SegmentHits,
  RenderContext,
  RenderedSectionResults,
} from './types'
import useAlgolia from './use-algolia'
import groupAndRenderSegments from './group-and-render-segments'
import {
  makeOnResultClick,
  logOnClearSearch,
  logHideSearch,
  logShowSearch,
  logOnResultsChange,
} from './analytics'
export type { AlgoliaApiKeys } from './types'

const ESCAPE_CODE = 'Escape'

function useShowHide(): [boolean, () => void, () => void] {
  const [isVisible, setIsVisible] = useState(false)
  const hide = useCallback((): void => {
    window.scroll(0, 0)
    setIsVisible(false)
  }, [])
  const show = useCallback((): void => {
    setIsVisible(true)
    logShowSearch()
  }, [])
  return [isVisible, show, hide]
}

type LocationListener = Parameters<ReturnType<typeof useHistory>['listen']>[0]

function useHistoryListener(listener: LocationListener): void {
  const history = useHistory()
  useEffect(() => (history.listen ? history.listen(listener) : () => null), [
    listener,
    history,
  ])
}

function processResults(
  query: string,
  context: RenderContext,
  segmentHits: SegmentHits
): { showNoResults: boolean; results: RenderedSectionResults[] } {
  const onResultClickHeading = makeOnResultClick(query)
  const results = groupAndRenderSegments({
    segmentHits,
    context,
    onResultClickHeading,
  })
  const showNoResults = query.length > 0 && results.length === 0
  return { showNoResults, results }
}

const Search: React.FC<ModalSearchProps> = props => {
  const { algoliaApiKeys, country, centre } = props
  const isEvolved = useEvolvedContext()
  const [isUiLoading, setIsUiLoading] = useState(false)
  const initialQuery = isEmbeddedSearchProps(props)
    ? props.showFixedQuery
    : undefined
  const {
    query,
    isLoading: isDataLoading,
    results: segmentHits,
    setQuery,
    isInitialised,
  } = useAlgolia(algoliaApiKeys, isEvolved, country, centre, initialQuery)
  const isLoading = isDataLoading || isUiLoading

  const [isVisible, show, hide] = useShowHide()
  const hideManual = (): void => {
    logHideSearch(query)
    hide()
  }
  useKeyPress({ [ESCAPE_CODE]: hideManual })
  useHistoryListener(hide) // If the URL changes hide the search modal

  const onSubmit = async (
    _event: React.FormEvent<unknown>,
    newQuery: string
  ): Promise<void> => {
    if (isEvolved && newQuery.length > 0 && typeof window !== 'undefined') {
      setIsUiLoading(true)
      const centrePath = centre ? `/${centre.slug}` : ''
      const q = encodeURIComponent(newQuery)
      // TODO when all clients are under one routing
      // use history.push from the router
      window.location.assign(`${centrePath}/search?q=${q}`)
      // // Required when using history.push:
      // hide() // in the event we're already on /search
      // setIsUiLoading(false)
    }
  }
  const context = { isEvolved, centre, country }
  const { results, showNoResults } = processResults(query, context, segmentHits)

  useEffect(() => {
    const numHits = results.reduce(
      (acc: number, { hits }: { hits: ReactNode[] }) => acc + hits.length,
      0
    )
    logOnResultsChange(query, numHits)
  }, [query, results])

  const displayMode = isVisible ? 'visibleModal' : 'hiddenModal'
  if (!isInitialised) {
    return null
  }

  return (
    <Fragment>
      <SearchContainer displayMode={displayMode}>
        <SearchModalControls
          queryText={query}
          onQueryChange={setQuery}
          onQueryKeyDown={() => null}
          onClickClose={hideManual}
          onClickClear={() => {
            logOnClearSearch(query)
            setQuery('')
          }}
          onSubmit={onSubmit}
        />
        <SearchResults
          results={results}
          showNoResults={showNoResults}
          isLoading={isLoading}
        />
      </SearchContainer>
      {props.children(show)}
    </Fragment>
  )
}

export const EmbeddedSearch: React.FC<EmbeddedSearchProps> = props => {
  const { country, centre, showFixedQuery, algoliaResults } = props
  const isEvolved = useEvolvedContext()

  const context = { isEvolved, centre, country }
  const { results, showNoResults } = processResults(
    showFixedQuery,
    context,
    algoliaResults || {}
  )

  return (
    <SearchContainer displayMode="embedded">
      <SearchResults
        isLoading={false}
        showNoResults={showNoResults}
        results={results}
        showFixedQuery={showFixedQuery}
      />
    </SearchContainer>
  )
}

const ModalSearch: React.FC<ModalSearchProps> = props => <Search {...props} />

export default ModalSearch
